import { FC, useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

interface OrganicSelectMenuProps {
  value: string;
  options: Array<{ value: string; label: string; title?: string }>;
  onChange: (_value: string) => void;
  className?: string;
  ariaLabel?: string;
}

const OrganicSelectMenu: FC<OrganicSelectMenuProps> = ({
  value,
  options,
  onChange,
  className = "",
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(option => option.value === value) ?? options[0];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  return (
    <div ref={rootRef} className={`organic-custom-select ${className}`.trim()}>
      <button
        type="button"
        className={`organic-custom-select__trigger ${open ? "organic-custom-select__trigger--open" : ""}`}
        onClick={() => setOpen(current => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={selected?.title ?? selected?.label}
      >
        <span className="organic-custom-select__trigger-label">{selected?.label}</span>
        <ChevronDown className="organic-custom-select__trigger-icon" />
      </button>
      {open ? (
        <div className="organic-custom-select__dropdown" role="listbox">
          {options.map(option => {
            const active = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`organic-custom-select__option ${active ? "organic-custom-select__option--active" : ""}`}
                title={option.title ?? option.label}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="organic-custom-select__option-check">
                  {active ? <Check size={16} /> : null}
                </span>
                <span className="organic-custom-select__option-label">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default OrganicSelectMenu;
